import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function CampaignInsights({ onStartTest }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/campaigns/insights")
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { /* silent — coach is a bonus, not a blocker */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading || dismissed || !data) return null;

    if (data.insufficient_data) {
        return (
            <div className="border border-[color:var(--espresso)] p-4 mb-6 text-xs text-[color:var(--cream-muted)]" data-testid="campaign-insights-empty">
                <span className="uppercase tracking-[0.24em] text-[color:var(--gold)] mr-2">A/B coach</span>
                {data.message}
            </div>
        );
    }

    const suggestions = data.suggestions || [];
    if (suggestions.length === 0) {
        return (
            <div className="border border-[color:var(--espresso)] p-4 mb-6 text-xs text-[color:var(--cream-muted)]" data-testid="campaign-insights-flat">
                <span className="uppercase tracking-[0.24em] text-[color:var(--gold)] mr-2">A/B coach</span>
                Your last {data.sample_size} sends performed similarly across subject styles (avg open rate {data.avg_open_rate}%). No dominant pattern yet — try a bolder A/B swing to surface a signal.
            </div>
        );
    }

    return (
        <div className="border border-[color:var(--gold)] p-5 mb-6 relative" data-testid="campaign-insights">
            <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 text-[color:var(--cream-muted)] hover:text-[color:var(--gold)] text-xs" data-testid="insights-dismiss" aria-label="Dismiss">✕</button>
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[color:var(--gold)]" />
                <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">A/B Coach — pattern from your last {data.sample_size} sends</p>
            </div>
            <p className="text-xs text-[color:var(--cream-muted)] mb-4">Average open rate across recent variants: <span className="text-[color:var(--gold)]">{data.avg_open_rate}%</span>. Here's what your list responds to:</p>
            <div className="space-y-3">
                {suggestions.map((s) => (
                    <div key={s.dimension} className="border border-[color:var(--espresso)] p-4" data-testid={`insight-${s.dimension}`}>
                        <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">{s.label}</p>
                        <p className="text-sm mt-1 text-[color:var(--soft-cream)]">
                            <strong className="text-[color:var(--gold)]">{s.winner_label}</strong> opened at <strong>{s.winner_open_rate}%</strong> vs {s.loser_open_rate}% for {s.loser_label.toLowerCase()} (<span className="text-[color:var(--gold)]">+{s.delta_pts} pts</span> · {s.sample_true} vs {s.sample_false} sends).
                        </p>
                        <p className="text-xs text-[color:var(--cream-muted)] mt-2 italic">{s.hypothesis}</p>
                        <div className="mt-3 grid md:grid-cols-2 gap-2 text-xs">
                            <div className="border border-[color:var(--espresso)]/60 px-3 py-2">
                                <span className="text-[0.55rem] uppercase tracking-[0.2em] text-[color:var(--cream-muted)]">Variant A</span>
                                <p className="text-[color:var(--soft-cream)] mt-1">{s.variant_a}</p>
                            </div>
                            <div className="border border-[color:var(--espresso)]/60 px-3 py-2">
                                <span className="text-[0.55rem] uppercase tracking-[0.2em] text-[color:var(--cream-muted)]">Variant B</span>
                                <p className="text-[color:var(--soft-cream)] mt-1">{s.variant_b}</p>
                            </div>
                        </div>
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={() => {
                                    onStartTest?.({ subject: s.variant_a, subject_b: s.variant_b, name: `A/B · ${s.label}` });
                                    toast.success(`Loaded a "${s.label}" A/B test into the composer.`);
                                }}
                                className="btn-gold text-[0.65rem]"
                                data-testid={`insight-start-${s.dimension}`}
                            >
                                Start this A/B test →
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
